import { useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ArrowRight, ChevronDown, Compass, Facebook, Instagram, Menu, MessageCircle, Mountain, ShieldCheck, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const LOGOS = {
  mask: "https://customer-assets.emergentagent.com/job_adventure-legacy/artifacts/6ri2xvy9_Sin%20t%C3%ADtulo-7-08%20%281%29.png",
  wordmark: "https://customer-assets.emergentagent.com/job_adventure-legacy/artifacts/yc4c2f8s_Sin%20t%C3%ADtulo-7-03%20%281%29.png",
};

const WHATSAPP_NUMBER = "593999700754";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;
const INSTAGRAM_URL = "https://www.instagram.com/la.ramona.motors/";
const FACEBOOK_URL = "https://www.facebook.com/LaRamonaMotorsCo/";

function whatsappLink(message) {
  return `${WHATSAPP_URL}?text=${encodeURIComponent(message)}`;
}

function openWhatsApp(message) {
  window.open(whatsappLink(message), "_blank", "noopener,noreferrer");
}

const IMAGES = {
  hero: "https://images.pexels.com/photos/19583236/pexels-photo-19583236.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
  fj40: "https://images.pexels.com/photos/20079552/pexels-photo-20079552.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1300",
  fj45: "https://images.pexels.com/photos/14918480/pexels-photo-14918480.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1300",
  fzj80: "https://images.pexels.com/photos/8206949/pexels-photo-8206949.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1300",
  uzj100: "https://images.unsplash.com/photo-1542362567-b07e54358753?auto=format&fit=crop&w=1300&q=80",
  defender: "https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=1300&q=80",
  mercedes: "https://images.unsplash.com/photo-1503736334956-4c8f8e92946d?auto=format&fit=crop&w=1300&q=80",
  lifestyleA: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80",
  lifestyleB: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1200&q=80",
  lifestyleC: "https://images.unsplash.com/photo-1516939884455-1445c8652f83?auto=format&fit=crop&w=1200&q=80",
  lifestyleD: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
};

const filters = ["Todos", "4x4 Clásico", "Colección", "Overland", "Restaurado", "Uso diario especial", "Archivo vendido"];

const vehicles = [
  {
    id: "fj40",
    name: "Toyota Land Cruiser FJ40",
    phrase: "Un ícono de montaña con alma mecánica.",
    status: "Disponible",
    tags: ["4x4 Clásico", "Restaurado", "Colección"],
    image: IMAGES.fj40,
    bullets: ["Caja manual y tracción robusta", "Presencia compacta para ruta real", "Restauración de criterio patrimonial"],
    story:
      "El FJ40 pertenece a esa categoría de máquinas que no necesitan explicar su legado. Su silueta corta, su ingeniería honesta y su capacidad fuera de camino lo convierten en una pieza viva: tan cómoda frente a un mirador del páramo como en una colección privada.",
    specs: ["Motor gasolina clásico", "Tracción 4x4", "Carrocería corta", "Configuración patrimonial"],
    use: "Ciudad con carácter, rutas de montaña, caminos de tierra y colección activa.",
  },
  {
    id: "fj45",
    name: "Toyota Land Cruiser FJ45",
    phrase: "Trabajo, viaje y legado en una misma plataforma.",
    status: "Bajo pedido",
    tags: ["4x4 Clásico", "Overland", "Restaurado"],
    image: IMAGES.fj45,
    bullets: ["Batalla larga y gran capacidad", "Ideal para proyecto overland", "Carácter utilitario de colección"],
    story:
      "El FJ45 tiene una presencia distinta: más pausada, más útil, más expedicionaria. Es una base noble para construir viajes largos, transportar equipo y contar una historia que se mide en kilómetros, polvo y conversaciones al borde del camino.",
    specs: ["Chasis largo", "Tracción 4x4", "Configuración pickup o wagon", "Preparación personalizable"],
    use: "Expediciones, playa, finca, montaña y construcción overland refinada.",
  },
  {
    id: "fzj80",
    name: "Toyota Land Cruiser FZJ80",
    phrase: "Confort noventero, capacidad seria y culto global.",
    status: "Disponible",
    tags: ["Overland", "Uso diario especial", "Colección"],
    image: IMAGES.fzj80,
    bullets: ["Plataforma legendaria para viaje", "Habitáculo amplio y sobrio", "Apreciado por conocedores"],
    story:
      "El FZJ80 marca un punto exacto entre lo analógico y lo cómodo. Es suficientemente refinado para usarlo a diario y suficientemente capaz para desaparecer hacia el páramo, la costa o una carretera secundaria sin perder compostura.",
    specs: ["Motor seis cilindros", "Ejes sólidos", "Interior familiar", "Preparación overland posible"],
    use: "Uso diario especial, ruta, montaña, viajes familiares y colección moderna.",
  },
  {
    id: "uzj100",
    name: "Toyota Land Cruiser UZJ100",
    phrase: "Lujo silencioso para llegar donde otros no llegan.",
    status: "Bajo pedido",
    tags: ["Overland", "Uso diario especial"],
    image: IMAGES.uzj100,
    bullets: ["Confort de gran turismo", "V8 con reserva de potencia", "Ideal para Ecuador diverso"],
    story:
      "El UZJ100 entiende la aventura desde la calma. Su fortaleza no grita: se siente en la ruta, en la lluvia, en la altura y en la confianza de avanzar con una máquina construida para durar.",
    specs: ["Motor V8", "Tracción integral", "Suspensión confortable", "Cabina premium"],
    use: "Quito, carretera, playa, montaña y viajes largos con comodidad.",
  },
  {
    id: "defender",
    name: "Land Rover Defender / Series",
    phrase: "Geometría británica, espíritu de expedición.",
    status: "Archivo de colección",
    tags: ["4x4 Clásico", "Colección", "Archivo vendido"],
    image: IMAGES.defender,
    bullets: ["Diseño funcional e inconfundible", "Herencia expedicionaria", "Pieza con presencia editorial"],
    story:
      "Un Defender o Series no se compra por comodidad inmediata. Se elige por una forma de mirar el camino: ángulos, aluminio, herramienta, ritual. Es una máquina que transforma cualquier llegada en escena.",
    specs: ["Carrocería clásica", "Tracción 4x4", "Configuraciones múltiples", "Restauración por criterio"],
    use: "Colección, fotografía, encuentros privados, rutas escénicas y aventura ligera.",
  },
  {
    id: "mercedes",
    name: "Mercedes-Benz clásico de colección",
    phrase: "Elegancia mecánica para conservar y vivir.",
    status: "Vendido",
    tags: ["Colección", "Restaurado", "Archivo vendido"],
    image: IMAGES.mercedes,
    bullets: ["Diseño atemporal", "Valor histórico y emocional", "Uso ceremonial o de fin de semana"],
    story:
      "Hay clásicos que no buscan barro, sino permanencia. Este Mercedes representa otra dimensión de La Ramona: la de vehículos con elegancia, técnica y una narrativa capaz de cruzar generaciones.",
    specs: ["Mecánica clásica", "Interior de época", "Conservación cuidada", "Perfil coleccionable"],
    use: "Colección, ciudad, eventos privados, ruta corta y archivo histórico.",
  },
];

const stories = [
  {
    title: "Rutas cerca de Quito para manejar un 4x4 con propósito",
    text: "Entre volcanes, caminos de tierra y cambios de clima, Quito permite vivir una máquina especial sin convertirla en objeto estático. La ruta correcta revela suspensión, motor, visibilidad y carácter.",
  },
  {
    title: "Por qué un Land Cruiser sigue siendo una máquina eterna",
    text: "Porque su valor no depende de una moda. Depende de ingeniería simple, reputación construida en territorios difíciles y una comunidad que entiende que durabilidad también puede ser lujo.",
  },
  {
    title: "Playa, montaña y páramo: el Ecuador como territorio 4x4",
    text: "Pocos países concentran tantos escenarios en tan poca distancia. Una unidad bien elegida permite pasar de la altura andina al aire salino con la misma naturalidad.",
  },
  {
    title: "Cómo elegir un vehículo de colección que también se pueda vivir",
    text: "La clave está en equilibrio: procedencia, estado, disponibilidad mecánica, honestidad estética y un propósito claro. El vehículo correcto no solo se mira; se conduce, se comparte y se recuerda.",
  },
];

const lifestyle = [
  { image: IMAGES.lifestyleA, label: "Dueños en ruta" },
  { image: IMAGES.lifestyleB, label: "Caminos de montaña" },
  { image: IMAGES.lifestyleC, label: "Encuentros privados" },
  { image: IMAGES.lifestyleD, label: "Territorio Ecuador" },
];

function scrollToSection(id) {
  const section = document.getElementById(id);
  if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Navigation() {
  const [open, setOpen] = useState(false);
  const items = [
    ["Vehículos", "vehiculos"],
    ["Historias", "historias"],
    ["La Experiencia", "experiencia"],
    ["Contacto", "contacto"],
  ];

  const handleNav = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header className="nav-shell" data-testid="main-navigation">
      <button className="brand-mark" onClick={() => handleNav("inicio")} data-testid="navigation-logo-button" aria-label="Ir al inicio">
        <img src={LOGOS.wordmark} alt="La Ramona Motors Co." data-testid="navigation-logo-image" />
      </button>
      <nav className="desktop-nav" data-testid="desktop-navigation-links">
        {items.map(([label, id]) => (
          <button key={id} onClick={() => handleNav(id)} data-testid={`navigation-${id}-button`}>
            {label}
          </button>
        ))}
      </nav>
      <a
        className="nav-whatsapp-cta"
        href={whatsappLink("Hola La Ramona Motors Co., quisiera hablar con un especialista sobre vehículos con historia.")}
        target="_blank"
        rel="noreferrer"
        data-testid="navigation-whatsapp-cta-link"
      >
        <MessageCircle size={15} /> Hablar con un especialista
      </a>
      <button className="mobile-menu-button" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle-button" aria-label="Abrir menú">
        {open ? <X size={22} /> : <Menu size={22} />}
      </button>
      {open && (
        <div className="mobile-panel" data-testid="mobile-navigation-panel">
          {items.map(([label, id]) => (
            <button key={id} onClick={() => handleNav(id)} data-testid={`mobile-navigation-${id}-button`}>
              {label}
              <ArrowRight size={16} />
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <header id="inicio" className="hero" data-testid="hero-section">
      <img className="hero-bg" src={IMAGES.hero} alt="Vehículos 4x4 clásicos en ruta overland por paisaje andino de Ecuador" data-testid="hero-background-image" fetchPriority="high" />
      <div className="hero-overlay" />
      <div className="hero-content">
        <Badge className="hero-badge" data-testid="hero-concept-badge">Máquinas con historia. Aventura con carácter.</Badge>
        <div className="hero-logo-lockup" data-testid="hero-title">
          <img className="hero-wordmark" src={LOGOS.wordmark} alt="La Ramona Motors Co. logo oficial" data-testid="hero-wordmark-image" />
          <img className="hero-mask" src={LOGOS.mask} alt="Isotipo máscara La Ramona Motors Co. estilo heritage overland" data-testid="hero-mask-image" />
        </div>
        <h1 className="hero-seo-title" data-testid="hero-subtitle">Vehículos 4x4 y de colección en Ecuador</h1>
        <p className="hero-support-copy" data-testid="hero-support-copy">Unidades curadas para quienes entienden la aventura, el legado y la exploración real.</p>
        <div className="hero-actions" data-testid="hero-action-buttons">
          <Button onClick={() => scrollToSection("vehiculos")} className="primary-cta" data-testid="hero-explore-vehicles-button">
            Descubrir más <ArrowRight size={18} />
          </Button>
          <Button
            onClick={() => openWhatsApp("Hola La Ramona Motors Co., quisiera hablar con un especialista sobre vehículos con historia, aventura y carácter.")}
            variant="outline"
            className="secondary-cta"
            data-testid="hero-contact-specialist-button"
          >
            Hablar con un especialista
          </Button>
        </div>
      </div>
      <button className="scroll-cue" onClick={() => scrollToSection("filosofia")} data-testid="hero-scroll-cue-button" aria-label="Bajar a filosofía">
        <span>Descubrir</span>
        <ChevronDown size={18} />
      </button>
    </header>
  );
}

function Philosophy() {
  return (
    <section id="filosofia" className="section philosophy" data-testid="philosophy-section">
      <div className="section-kicker" data-testid="philosophy-kicker">Criterio curatorial</div>
      <div className="philosophy-grid">
        <h2 data-testid="philosophy-title">Exploración & Heritage</h2>
        <div className="philosophy-copy">
          <h3 data-testid="philosophy-subtitle">No vendemos autos. Curamos máquinas con historia.</h3>
          <p data-testid="philosophy-copy-main">
            La Ramona Motors selecciona vehículos por carácter, capacidad, legado y experiencia. Cada unidad debe sentirse como una pieza con identidad propia: lista para caminos reales, viajes memorables y colección.
          </p>
          <div className="principles" data-testid="philosophy-principles">
            <span><ShieldCheck size={18} /> Procedencia</span>
            <span><Compass size={18} /> Exploración</span>
            <span><Mountain size={18} /> Territorio</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function VehicleCard({ vehicle, onSelect }) {
  return (
    <article className="vehicle-card" data-testid={`vehicle-card-${vehicle.id}`}>
      <div className="vehicle-image-wrap" data-testid={`vehicle-image-wrap-${vehicle.id}`}>
        <img src={vehicle.image} alt={`${vehicle.name} curado por La Ramona Motors Co. para exploración overland y colección en Ecuador`} data-testid={`vehicle-image-${vehicle.id}`} loading="lazy" />
        <span className="reference-label" data-testid={`vehicle-reference-label-${vehicle.id}`}>Imagen referencial</span>
        <span className="status-pill" data-testid={`vehicle-status-${vehicle.id}`}>{vehicle.status}</span>
      </div>
      <div className="vehicle-body">
        <h3 className="vehicle-model" data-testid={`vehicle-name-${vehicle.id}`}>{vehicle.name}</h3>
        <p className="vehicle-phrase" data-testid={`vehicle-phrase-${vehicle.id}`}>{vehicle.phrase}</p>
        <ul data-testid={`vehicle-attributes-${vehicle.id}`}>
          {vehicle.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
        <button className="story-button" onClick={() => onSelect(vehicle)} data-testid={`vehicle-story-button-${vehicle.id}`}>
          Conocer su historia <ArrowRight size={16} />
        </button>
        <a
          className="vehicle-whatsapp-link"
          href={whatsappLink(`Hola La Ramona Motors Co., quisiera consultar disponibilidad del ${vehicle.name}.`)}
          target="_blank"
          rel="noreferrer"
          data-testid={`vehicle-whatsapp-link-${vehicle.id}`}
        >
          Consultar disponibilidad
        </a>
      </div>
    </article>
  );
}

function Vehicles() {
  const [activeFilter, setActiveFilter] = useState("Todos");
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const filteredVehicles = useMemo(() => {
    if (activeFilter === "Todos") return vehicles;
    return vehicles.filter((vehicle) => vehicle.tags.includes(activeFilter));
  }, [activeFilter]);

  return (
    <section id="vehiculos" className="section vehicles-section" data-testid="vehicles-section">
      <div className="section-header wide">
        <div>
          <div className="section-kicker" data-testid="vehicles-kicker">Inventario curado</div>
          <h2 data-testid="vehicles-title">Vehículos Destacados</h2>
        </div>
        <p data-testid="vehicles-intro">Una selección aspiracional de unidades con legado, presencia y propósito. No se muestran como catálogo; se presentan como historias listas para continuar.</p>
      </div>

      <div className="filter-bar" data-testid="vehicle-filter-bar">
        {filters.map((filter) => (
          <button
            key={filter}
            className={activeFilter === filter ? "active" : ""}
            onClick={() => setActiveFilter(filter)}
            data-testid={`vehicle-filter-${filter.toLowerCase().replaceAll(" ", "-")}-button`}
          >
            {filter}
          </button>
        ))}
      </div>

      <div className="vehicle-grid" data-testid="vehicle-grid">
        {filteredVehicles.map((vehicle) => (
          <VehicleCard key={vehicle.id} vehicle={vehicle} onSelect={setSelectedVehicle} />
        ))}
      </div>

      <Dialog open={!!selectedVehicle} onOpenChange={(isOpen) => !isOpen && setSelectedVehicle(null)}>
        <DialogContent className="vehicle-modal" data-testid="vehicle-story-modal">
          {selectedVehicle && (
            <>
              <DialogHeader>
                <DialogTitle asChild>
                  <h2 data-testid="vehicle-modal-title">{selectedVehicle.name}</h2>
                </DialogTitle>
                <DialogDescription className="modal-description" data-testid="vehicle-modal-description">
                  Historia, especificaciones y potencial de uso de esta unidad curada por La Ramona Motors Co.
                </DialogDescription>
              </DialogHeader>
              <div className="modal-grid">
                <img src={selectedVehicle.image} alt={`${selectedVehicle.name} con historia, capacidades 4x4 y potencial de colección en Ecuador`} data-testid="vehicle-modal-image" loading="lazy" />
                <div>
                  <Badge className="modal-status" data-testid="vehicle-modal-status">{selectedVehicle.status}</Badge>
                  <h3 data-testid="vehicle-modal-story-title">Historia y capacidades</h3>
                  <p className="modal-story" data-testid="vehicle-modal-story">{selectedVehicle.story}</p>
                  <h3 data-testid="vehicle-modal-specs-title">Especificaciones técnicas</h3>
                  <ul className="modal-specs" data-testid="vehicle-modal-specs-list">
                    {selectedVehicle.specs.map((spec) => (
                      <li key={spec}>{spec}</li>
                    ))}
                  </ul>
                  <h3 data-testid="vehicle-modal-use-title">Exploración y uso ideal</h3>
                  <p className="modal-use" data-testid="vehicle-modal-use-copy">{selectedVehicle.use}</p>
                  <Button
                    onClick={() => openWhatsApp(`Hola La Ramona Motors Co., quisiera agendar una conversación sobre el ${selectedVehicle.name}.`)}
                    className="primary-cta modal-cta"
                    data-testid="vehicle-modal-schedule-button"
                  >
                    Agendar una conversación <MessageCircle size={17} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Stories() {
  return (
    <section id="historias" className="section stories-section" data-testid="stories-section">
      <div className="section-header">
        <div className="section-kicker" data-testid="stories-kicker">Historias & Expediciones</div>
        <h2 data-testid="stories-title">Historias & Expediciones</h2>
        <p data-testid="stories-intro">El vehículo correcto cambia la forma de mirar el territorio.</p>
      </div>
      <Accordion type="single" collapsible className="story-accordion" data-testid="stories-accordion">
        {stories.map((story, index) => (
          <AccordionItem value={`story-${index}`} key={story.title} data-testid={`story-item-${index}`}>
            <AccordionTrigger data-testid={`story-trigger-${index}`}>{story.title}</AccordionTrigger>
            <AccordionContent data-testid={`story-content-${index}`}>{story.text}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function Experience() {
  const items = [
    "Búsqueda personalizada de unidades especiales",
    "Consignación curada con narrativa y criterio visual",
    "Asesoría para compra de vehículos con legado",
    "Archivo de unidades vendidas y referencias de mercado",
    "Conexión con exploradores y coleccionistas",
  ];

  return (
    <section id="experiencia" className="section experience-section" data-testid="experience-section">
      <div className="experience-panel">
        <div>
          <div className="section-kicker" data-testid="experience-kicker">La Experiencia La Ramona</div>
          <h2 data-testid="experience-title">La Experiencia La Ramona</h2>
          <h3 data-testid="experience-subtitle">Para quienes no buscan cualquier vehículo, sino el correcto.</h3>
          <p data-testid="experience-copy">Acompañamos decisiones que mezclan emoción, técnica y pertenencia. La unidad ideal no aparece por volumen; aparece por criterio.</p>
        </div>
        <div className="experience-list" data-testid="experience-list">
          {items.map((item, index) => (
            <div className="experience-item" key={item} data-testid={`experience-item-${index}`}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <p>{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Lifestyle() {
  const stats = [
    ["48+", "Vehículos curados"],
    ["48+", "Historias entregadas"],
    ["12k", "Rutas recorridas"],
    ["120+", "Coleccionistas conectados"],
  ];

  return (
    <section className="section lifestyle-section" data-testid="lifestyle-section">
      <div className="section-header wide">
        <div>
          <div className="section-kicker" data-testid="lifestyle-kicker">Comunidad / Lifestyle</div>
          <h2 data-testid="lifestyle-title">Comunidad</h2>
          <h3 data-testid="lifestyle-subtitle">Aventura elegante, vivida sin ruido.</h3>
        </div>
        <p data-testid="lifestyle-copy">Rutas privadas, detalles mecánicos, interiores con memoria y encuentros donde el vehículo es punto de partida, no espectáculo.</p>
      </div>
      <div className="lifestyle-grid" data-testid="lifestyle-gallery">
        {lifestyle.map((item, index) => (
          <figure key={item.label} className={index === 0 ? "large" : ""} data-testid={`lifestyle-gallery-item-${index}`}>
            <img src={item.image} alt={`${item.label} de comunidad overland premium y vehículos con historia en Ecuador`} data-testid={`lifestyle-gallery-image-${index}`} loading="lazy" />
            <figcaption data-testid={`lifestyle-gallery-caption-${index}`}>{item.label}</figcaption>
          </figure>
        ))}
      </div>
      <div className="stats-strip" data-testid="lifestyle-stats">
        {stats.map(([value, label]) => (
          <div key={label} data-testid={`lifestyle-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", whatsapp: "", vehicle: "", message: "" });

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitToWhatsApp = (event) => {
    event.preventDefault();
    const text = `Hola La Ramona Motors Co., soy ${form.name || ""}. Mi WhatsApp es ${form.whatsapp || ""}. Busco: ${form.vehicle || "un vehículo especial"}. ${form.message || "Quisiera iniciar una conversación."}`;
    openWhatsApp(text);
  };

  return (
    <section id="contacto" className="section contact-section" data-testid="contact-section">
      <div className="contact-grid">
        <div className="contact-copy">
          <div className="section-kicker" data-testid="contact-kicker">Quito, Ecuador</div>
          <h2 data-testid="contact-title">Contacto</h2>
          <h3 data-testid="contact-subtitle">Habla con un especialista</h3>
          <p data-testid="contact-copy-text">Explora vehículos con historia, aventura y carácter en Ecuador.</p>
          <div className="social-links" data-testid="contact-social-links">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="contact-instagram-link"><Instagram size={16} /> Instagram</a>
            <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" data-testid="contact-facebook-link"><Facebook size={16} /> Facebook</a>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"><MessageCircle size={16} /> WhatsApp</a>
          </div>
        </div>
        <form className="contact-form" onSubmit={submitToWhatsApp} data-testid="contact-form">
          <div>
            <Label htmlFor="name" data-testid="contact-name-label">Nombre</Label>
            <Input id="name" value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Tu nombre" data-testid="contact-name-input" required />
          </div>
          <div>
            <Label htmlFor="whatsapp" data-testid="contact-whatsapp-label">WhatsApp</Label>
            <Input id="whatsapp" value={form.whatsapp} onChange={(event) => updateField("whatsapp", event.target.value)} placeholder="+593" data-testid="contact-whatsapp-input" required />
          </div>
          <div>
            <Label htmlFor="vehicle" data-testid="contact-vehicle-label">Vehículo de interés</Label>
            <Input id="vehicle" value={form.vehicle} onChange={(event) => updateField("vehicle", event.target.value)} placeholder="Land Cruiser, Defender, clásico de colección..." data-testid="contact-vehicle-input" />
          </div>
          <div>
            <Label htmlFor="message" data-testid="contact-message-label">Mensaje</Label>
            <Textarea id="message" value={form.message} onChange={(event) => updateField("message", event.target.value)} placeholder="Cuéntanos qué historia quieres construir." data-testid="contact-message-textarea" />
          </div>
          <Button type="submit" className="primary-cta form-cta" data-testid="contact-submit-button">
            Iniciar conversación <MessageCircle size={17} />
          </Button>
        </form>
      </div>
    </section>
  );
}

function Home() {
  return (
    <main className="site-shell" data-testid="site-shell">
      <Navigation />
      <Hero />
      <Philosophy />
      <Vehicles />
      <Stories />
      <Experience />
      <Lifestyle />
      <Contact />
      <footer className="site-footer" data-testid="site-footer">
        <img src={LOGOS.mask} alt="Sello La Ramona Motors Co." data-testid="footer-logo-image" />
        <span data-testid="footer-brand-text">La Ramona Motors Co. · Máquinas con historia. Aventura con carácter.</span>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          className="footer-whatsapp-link"
          data-testid="footer-whatsapp-link"
        >
          <MessageCircle size={15} /> Solicitar información
        </a>
      </footer>
      <a
        href={whatsappLink("Hola La Ramona Motors Co., quisiera hablar con un especialista.")}
        target="_blank"
        rel="noreferrer"
        className="mobile-sticky-whatsapp"
        data-testid="mobile-sticky-whatsapp-link"
      >
        <MessageCircle size={16} /> Hablar con un especialista
      </a>
    </main>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
